import { cloneDeep } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { AxiosResponse } from "axios";
import * as gql from "gql-query-builder";
import { AddressVerified, addressVerifiedKeys, googleAddressCountries, PlaceAddress } from "../../../../../utils/appInterfaces/AppInterfaces";
import { AddressLocation, PayloadFormatAddress } from "./GoogleAutocompleteAddressTypes";
import { AppAddressCountries, GoogleAddressCountries } from "../../../../../utils/appEnums/AppEnums";
import { BO_CORE_DEFAULT_NUMBER_VALUE, BO_CORE_DEFAULT_VALUE, isEmptyStringValue } from "../../../../../utils/AppUtils";
import { graphQlRequest } from "../../../../../config/axios/apis/graphql/graphql";

const googleAddressCountriesArr = [GoogleAddressCountries.UNITED_STATES_OF_AMERICA, GoogleAddressCountries.CANADA, GoogleAddressCountries.AUSTRALIA];

/**
 * NG-7511: the existing "never geocoded" coordinate sentinel, named so the manual-entry adapters and
 * this mapper agree on one value instead of each hardcoding 999.9.
 */
export const DEGRADED_PLACE_COORDINATE = 999.9;

export const defaultAddressLocation: AddressLocation = {
    id: BO_CORE_DEFAULT_NUMBER_VALUE,
    addressLine1: "",
    addressLine2: "",
    fullAddress: "",
    city: "",
    country: "",
    county: "",
    isVerified: false,
    lat: DEGRADED_PLACE_COORDINATE,
    lon: DEGRADED_PLACE_COORDINATE,
    state: "",
    type: "",
    zipCode: "",
};

/**
 * NG-8840: the seed for an address the user has just entered and that has never been persisted.
 *
 * `defaultAddressLocation.id` is the BO_CORE_DEFAULT_NUMBER_VALUE (-1) "not set" sentinel, which is
 * right almost everywhere - but the endpoints that accept a whole `AddressLocation` (the ordering
 * journey's step submit, `PUT /organization`) resolve `id` as a real Location primary key. Sending
 * -1 there makes the tenant factory fail with "An instance with id -1 of type Location does not
 * exist" and 400 the request; `null` is what those endpoints expect for "create this one".
 *
 * Use this - not `{ ...defaultAddressLocation }` - whenever you are building an address the user has
 * not saved yet, so the next consumer does not have to rediscover NG-8840.
 */
export const newAddressLocation: AddressLocation = {
    ...defaultAddressLocation,
    // @ts-expect-error -- AddressLocation.id is typed `number` because several consumers still index
    // by it; widening it to `number | null` is a separate cleanup (see NG-8840 review notes).
    id: null,
};

/**
 * Narrows to an id that actually identifies a persisted Location, excluding both "absent" and the
 * BO_CORE_DEFAULT_NUMBER_VALUE (-1) sentinel a form seeded from `defaultAddressLocation` holds.
 */
export const isSavedLocationId = (id: number | null | undefined): id is number => id !== null && id !== undefined && id !== BO_CORE_DEFAULT_NUMBER_VALUE;

export const getAddressValuesFromPlacesApi = (locationValue: AddressLocation, placeAddressValue: PlaceAddress): { location: AddressLocation } => {
    const locationCopy = cloneDeep(locationValue);

    locationCopy.addressLine1 = `${placeAddressValue.street_number.long_name} ${placeAddressValue.route.long_name}`;
    locationCopy.addressLine2 = "";
    locationCopy.city = placeAddressValue.locality.long_name;
    locationCopy.county = placeAddressValue.administrative_area_level_2.long_name;
    locationCopy.lat = placeAddressValue.lat;
    locationCopy.lon = placeAddressValue.lon;
    locationCopy.state = placeAddressValue.administrative_area_level_1.short_name;
    locationCopy.zipCode = placeAddressValue.postal_code.long_name;
    locationCopy.fullAddress = placeAddressValue.formatted_address;
    // NG-7511: this mapper never assigned `country`, so it only ever survived because the host form
    // seeded it before the first result landed. A manually-entered address goes through the same
    // mapper (via a synthesized PlaceAddress), and on hosts that don't pre-seed — or that clobber the
    // seed from a stale render — country reached the API empty and the backend rejected the submit
    // with "is not a valid value for attribute: Country".
    //
    // Scoped deliberately to the manual path, which needs BOTH signals to identify: `defaultPlaceAddress`
    // also carries the degraded-coordinate sentinel, so coordinates alone do not distinguish a manual
    // address from an empty one. Only `manualAddressToPlaceAddress` additionally sets a non-empty
    // country `short_name` (the host's configured `AppAddressCountries` value); the default place
    // leaves it "". Real Google results carry real coordinates and are left exactly as before, so the
    // healthy path cannot shift to Google's own country spelling.
    const hasDegradedCoordinates = placeAddressValue.lat === DEGRADED_PLACE_COORDINATE && placeAddressValue.lon === DEGRADED_PLACE_COORDINATE;
    const manualCountry = placeAddressValue.country?.short_name ?? "";
    if (hasDegradedCoordinates && manualCountry && !locationCopy.country) {
        locationCopy.country = manualCountry;
    }
    return { location: locationCopy };
};

export const checkNewGoogleAddressIsVerified = (locationValue: AddressLocation, placeAddressValue: PlaceAddress): boolean => {
    const newAddressVerified: AddressVerified = {
        country: true,
        city: true,
        zipCode: true,
        fullAddress: true,
    };
    const keys: string[] = addressVerifiedKeys;
    keys.forEach((key: string) => {
        if (key === "country") {
            let selectedCountry: string | undefined;
            if (locationValue[key]) {
                selectedCountry = locationValue[key].trim();
                switch (selectedCountry) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                    case AppAddressCountries.UNITED_STATES_OF_AMERICA:
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                        newAddressVerified[key] = placeAddressValue[key]?.long_name === GoogleAddressCountries.UNITED_STATES_OF_AMERICA;
                        break;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                    case AppAddressCountries.CANADA:
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                        newAddressVerified[key] = placeAddressValue[key]?.long_name === GoogleAddressCountries.CANADA;
                        break;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                    case AppAddressCountries.AUSTRALIA:
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
                        newAddressVerified[key] = placeAddressValue[key]?.long_name === GoogleAddressCountries.AUSTRALIA;
                        break;
                    default:
                        break;
                }
            } else {
                newAddressVerified[key] = false;
            }
        }
        if (key === "city") {
            if (placeAddressValue.locality.long_name.length > 0) {
                newAddressVerified[key] = locationValue[key].trim().length > 0 && locationValue[key].trim() === placeAddressValue.locality.long_name;
            }
        }
        if (key === "zipCode") {
            newAddressVerified[key] = locationValue[key].trim().length > 0 && locationValue[key].trim() === placeAddressValue.postal_code.long_name;
        }
        if (key === "fullAddress") {
            newAddressVerified[key] = locationValue[key].trim().length > 0 && locationValue[key].trim() === placeAddressValue.formatted_address;
        }
    });

    return newAddressVerified.country && newAddressVerified.city && newAddressVerified.zipCode && newAddressVerified.fullAddress;
};

export const createNewLocationRecord = (locationValue: AddressLocation, addressValue: string, locationName: string): Promise<AxiosResponse> => {
    const defaultVerified =
        (locationValue.lat !== 999.9 && locationValue.lon !== 999.9 && locationValue.isVerified) ||
        (googleAddressCountriesArr.includes(locationValue.country as GoogleAddressCountries) && locationValue.isVerified);

    const addressLine1 = locationValue.addressLine1?.trim().length > 0 ? locationValue.addressLine1?.trim() : addressValue.trim();
    const fullAddress = locationValue.fullAddress.trim().length > 0 ? locationValue.fullAddress.trim() : addressValue.trim();

    const locationUpdateValue = {
        type: locationValue.type,
        name: `${locationName}-(${uuidv4()})`,
        country: locationValue.country,
        addressLine1,
        addressLine2: locationValue.addressLine2.trim() || BO_CORE_DEFAULT_VALUE,
        cityName: locationValue.city?.trim() || BO_CORE_DEFAULT_VALUE,
        county: locationValue.county.trim() || BO_CORE_DEFAULT_VALUE,
        stateName: locationValue.state.trim() || BO_CORE_DEFAULT_VALUE,
        postalCode: locationValue.zipCode.trim() || BO_CORE_DEFAULT_VALUE,
        lat: locationValue.lat,
        lon: locationValue.lon,
        countryCode: "",
        isValidated: defaultVerified,
        fullAddress,
    };

    const query = gql.mutation([
        {
            operation: "InsertLocationRecord",
            variables: {
                record: {
                    value: locationUpdateValue,
                    type: "GraphQLInsertLocationInput",
                },
            },
            fields: [{ record: ["id"] }],
        },
    ]);
    return graphQlRequest(query, false);
};

export const createNewPatientAddress = (addressValue: string, locationValue: AddressLocation): PayloadFormatAddress | null => {
    const defaultVerified =
        (locationValue.lat !== 999.9 && locationValue.lon !== 999.9 && locationValue.isVerified) ||
        (!googleAddressCountries.includes(locationValue.country as GoogleAddressCountries) && locationValue.isVerified);
    const defaultAddressLine1 = locationValue.addressLine1?.trim().length > 0 ? locationValue.addressLine1.trim() : addressValue?.trim();
    const defaultAddressLine2 = locationValue.addressLine2?.trim().length > 0 ? locationValue.addressLine2.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultCityName = locationValue.city.trim().length > 0 ? locationValue.city.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultCounty = locationValue.county?.trim().length > 0 ? locationValue.county.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultPostalCode = locationValue.zipCode?.trim().length > 0 ? locationValue.zipCode.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultStateName = locationValue.state?.trim().length > 0 ? locationValue.state.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultFullAddress = locationValue.fullAddress?.trim().length > 0 ? locationValue.fullAddress.trim() : addressValue?.trim();
    const addressAsString = addressValue ?? "";
    if (isEmptyStringValue(addressAsString)) {
        return null;
    }

    let countryCode;

    switch (locationValue.country) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
        case AppAddressCountries.CANADA:
            countryCode = "ca";
            break;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
        case AppAddressCountries.UNITED_STATES_OF_AMERICA:
            countryCode = "us";
            break;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- enum-comparison-debt: country name is free-form from GET /public/countries, long_name is third-party Google Places data
        case AppAddressCountries.AUSTRALIA:
            countryCode = "au";
            break;
        default:
            countryCode = BO_CORE_DEFAULT_VALUE;
    }

    return {
        addressLine1: defaultAddressLine1,
        addressLine2: defaultAddressLine2,
        cityName: defaultCityName,
        country: locationValue.country,
        countryCode,
        county: defaultCounty,
        lat: locationValue.lat,
        lon: locationValue.lon,
        name: `${addressValue}-(${uuidv4()})`,
        postalCode: defaultPostalCode,
        stateName: defaultStateName,
        type: "Personal",
        personLocationType: [locationValue.type],
        validated: defaultVerified,
        fullAddress: defaultFullAddress,
    };
};

export const createNewOrganizationAddress = (addressValue: string, locationValue: AddressLocation): PayloadFormatAddress => {
    const defaultVerified =
        (locationValue.lat !== 999.9 && locationValue.lon !== 999.9 && locationValue.isVerified) ||
        (!googleAddressCountries.includes(locationValue.country as GoogleAddressCountries) && locationValue.isVerified);
    const defaultAddressLine1 = locationValue.addressLine1.trim().length > 0 ? locationValue.addressLine1.trim() : addressValue.trim();
    const defaultAddressLine2 = locationValue.addressLine2.trim().length > 0 ? locationValue.addressLine2.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultCityName = locationValue.city?.trim().length > 0 ? locationValue.city?.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultCounty = locationValue.county.trim().length > 0 ? locationValue.county.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultPostalCode = locationValue.zipCode.trim().length > 0 ? locationValue.zipCode.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultStateName = locationValue.state.trim().length > 0 ? locationValue.state.trim() : BO_CORE_DEFAULT_VALUE;
    const defaultFullAddress = locationValue.fullAddress.trim().length > 0 ? locationValue.fullAddress.trim() : addressValue.trim();

    return {
        addressLine1: defaultAddressLine1,
        addressLine2: defaultAddressLine2,
        cityName: defaultCityName,
        country: locationValue.country,
        countryCode: BO_CORE_DEFAULT_VALUE,
        county: defaultCounty,
        lat: locationValue.lat,
        lon: locationValue.lon,
        name: `${addressValue}-(${uuidv4()})`,
        postalCode: defaultPostalCode,
        stateName: defaultStateName,
        type: locationValue.type,
        validated: defaultVerified,
        fullAddress: defaultFullAddress,
    };
};
